import { getMetrics } from './metrics';
import { ErrorLocation, reportClientError } from './errors';

function classifyResourceTarget(target: EventTarget | null): string {
  if (target instanceof HTMLImageElement) return 'img';
  if (target instanceof HTMLScriptElement) return 'script';
  if (target instanceof HTMLLinkElement) return 'link';
  if (target instanceof HTMLMediaElement) return 'media';
  return 'other';
}

function isResourceErrorEvent(event: ErrorEvent): boolean {
  return event.target instanceof HTMLElement && !event.error;
}

function handleWindowError(event: ErrorEvent): void {
  if (isResourceErrorEvent(event)) {
    try {
      const targetType = classifyResourceTarget(event.target);
      getMetrics().spaResourceLoadFailures.add(1, { target_type: targetType });
    } catch (err) {
      reportClientError(err, ErrorLocation.ResourceLoadError);
    }
    return;
  }
  reportClientError(event.error ?? event.message, ErrorLocation.WindowError);
}

function handleUnhandledRejection(event: PromiseRejectionEvent): void {
  reportClientError(event.reason, ErrorLocation.UnhandledRejection);
}

function handleVisibilityChange(): void {
  try {
    if (document.visibilityState === 'hidden') {
      getMetrics().spaTabBackgrounded.add(1);
    }
  } catch (err) {
    reportClientError(err, ErrorLocation.VisibilityHandlerFailed);
  }
}

function installLongtaskObserver(): void {
  if (typeof PerformanceObserver === 'undefined') return;
  try {
    const observer = new PerformanceObserver(list => {
      const metrics = getMetrics();
      for (const entry of list.getEntries()) {
        metrics.spaLongtasks.add(1);
        metrics.spaLongtaskDurationMs.record(entry.duration);
      }
    });
    observer.observe({ entryTypes: ['longtask'] });
  } catch (err) {
    reportClientError(err, ErrorLocation.LongtaskObserverFailed);
  }
}

export function initSpaObservers(): void {
  installLongtaskObserver();
  window.addEventListener('error', handleWindowError, { capture: true });
  window.addEventListener('unhandledrejection', handleUnhandledRejection);
  document.addEventListener('visibilitychange', handleVisibilityChange);
}
